import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiUpload, FiArrowRight, FiInfo, FiUser, FiLock } from 'react-icons/fi';
import RegisterModal from '../components/RegisterModal';
import { useToast } from '../contexts/ToastContext';

// Container principale con lo stesso gradient del resto del sito
const DemoContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 70px);
  width: 100%;
  position: relative;
  overflow-x: hidden;
  background: linear-gradient(145deg, #1a0c2e 0%, #2c0f40 60%, #3a0e50 100%);
  color: #f0f0f0;
  font-family: 'Poppins', sans-serif;
  padding: 0;
  align-items: center;
`;

// Stili principali
const PageContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const AnalysisConfigSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  width: 100%;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const AnalysisCard = styled.div`
  background: #1e0b2d;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(128, 0, 128, 0.15);
`;

const CardHeader = styled.div`
  padding: 15px 20px;
  border-bottom: 1px solid rgba(128, 0, 128, 0.15);
  display: flex;
  align-items: center;
  gap: 10px;
  color: #f8f8f8;
  
  svg {
    color: #b08aa8;
  }
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const CardContent = styled.div`
  flex: 1;
  padding: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const UploadArea = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
  background: rgba(30, 11, 45, 0.5);
  
  &:hover {
    background: rgba(30, 11, 45, 0.7);
  }
`;

const UploadIcon = styled.div`
  width: 70px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  
  svg {
    color: #b08aa8;
    opacity: 0.8;
    font-size: 40px;
  }
`;

const InstagramIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
  </svg>
);

const UploadText = styled.p`
  text-align: center;
  color: #f0f0f0;
  margin: 10px 0;
  font-size: 0.95rem;
`;

const FileInput = styled.input`
  display: none;
`;

const ImagePreviewArea = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  width: 100%;
  padding: 30px;
`;

const ImagePreview = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background-size: cover;
  background-position: center;
  border-radius: 10px;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &::after {
    content: '×';
    position: absolute;
    top: 5px;
    right: 5px;
    width: 22px;
    height: 22px;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 18px;
    opacity: 0;
    transition: opacity 0.2s;
  }
  
  &:hover::after {
    opacity: 1;
  }
`;

const CardFooter = styled.div`
  padding: 15px 20px;
  border-top: 1px solid rgba(128, 0, 128, 0.15);
`;

const ActionButton = styled.button`
  background: #ff56c9;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 12px 24px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: none;
  margin: 0 auto;
  
  svg {
    color: white;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 86, 201, 0.2);
    background: #ff65d5;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// Lato destro - Personalizzazione
const CustomizeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 30px 0;
  font-size: 1.8rem;
  font-weight: 700;
  color: #f8f8f8;
  font-family: 'Bubblegum Sans', 'Poppins', sans-serif;
`;

const ContextSelector = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled.label`
  font-size: 1rem;
  color: #f0f0f0;
  margin-bottom: 5px;
`;

const Select = styled.div`
  position: relative;
  width: 100%;
  
  select {
    width: 100%;
    padding: 16px 20px;
    appearance: none;
    background: #1e0b2d;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 10px;
    color: #f0f0f0;
    font-size: 1rem;
    cursor: pointer;
    
    &:focus {
      outline: none;
      border-color: rgba(255, 64, 192, 0.5);
    }
  }
  
  &::after {
    content: '▼';
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(255, 255, 255, 0.5);
    pointer-events: none;
    font-size: 0.8rem;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 16px 20px;
  background: #1e0b2d;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  color: #f0f0f0;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: rgba(255, 64, 192, 0.5);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const DisclaimerBox = styled.div`
  margin-top: 20px;
  padding: 20px;
  border-radius: 10px;
  border-left: 4px solid #ff40c0;
  background: rgba(30, 11, 45, 0.5);
  display: flex;
  gap: 12px;
  
  svg {
    color: #ff40c0;
    min-width: 20px;
    margin-top: 3px;
  }
`;

const DisclaimerText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.5;
  
  span {
    color: #ff40c0;
    font-weight: 500;
  }
`;

const AnalyzeButton = styled.button`
  width: 100%;
  background: #d68300;
  color: white;
  border: none;
  border-radius: 30px;
  padding: 16px 20px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: none;
  margin-top: 20px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(214, 131, 0, 0.2);
    background: #e69000;
  }
  
  svg {
    font-size: 1.2rem;
  }
`;

const CloudUploadIcon = () => (
  <svg width="70" height="70" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 16.5L12 11.5L17 16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 11.5V20.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M20.39 18.39C21.3654 17.8583 22.1359 17.0169 22.5799 15.9986C23.024 14.9804 23.1162 13.8432 22.8422 12.7667C22.5682 11.6901 21.9434 10.7355 21.0666 10.0534C20.1898 9.37137 19.1108 9.00072 18 8.99998H16.74C16.4373 7.82923 15.8731 6.74232 15.0899 5.82098C14.3067 4.89964 13.3248 4.16785 12.2181 3.68059C11.1114 3.19334 9.90856 2.96584 8.70012 3.01638C7.49169 3.06691 6.31389 3.39346 5.24998 3.97044C4.18607 4.54741 3.26198 5.35983 2.53763 6.34976C1.81328 7.33969 1.30645 8.47883 1.05422 9.68874C0.801996 10.8986 0.810177 12.1491 1.07784 13.3548C1.34551 14.5605 1.86003 15.691 2.58998 16.67" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Aggiungo i componenti per l'analisi parziale
const AnalysisResultsSection = styled.div`
  margin-top: 40px;
  background: rgba(25, 8, 35, 0.5);
  border-radius: 20px;
  padding: 30px;
  border: 1px solid rgba(255, 64, 192, 0.15);
  width: 100%;
  max-width: 1100px;
  margin: 40px auto 0;
  display: ${props => (props.show ? 'block' : 'none')};
`;

const AnalysisTitle = styled.h2`
  font-size: 2.2rem;
  font-weight: 700;
  color: #ff40c0;
  text-align: center;
  margin-bottom: 30px;
  font-family: 'Bubblegum Sans', 'Poppins', sans-serif;
`;

const AnalysisIntro = styled.p`
  font-size: 1.1rem;
  color: #f0f0f0;
  text-align: center;
  margin-bottom: 30px;
  font-style: italic;
`;

const AnalysisParagraph = styled.p`
  font-size: 1.05rem;
  color: #f0f0f0;
  line-height: 1.6;
  margin-bottom: 20px;
  text-align: center;
`;

const PersonalityList = styled.div`
  margin: 25px 0;
`;

const PersonalityItem = styled.div`
  margin-bottom: 20px;
  display: flex;
  align-items: flex-start;
`;

const PersonalityEmoji = styled.span`
  font-size: 1.4rem;
  margin-right: 10px;
  color: #ff40c0;
`;

const PersonalityContent = styled.div`
  flex: 1;
`;

const PersonalityTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 600;
  color: #ff40c0;
  margin-bottom: 8px;
`;

const PersonalityDescription = styled.p`
  font-size: 1rem;
  color: #f0f0f0;
  line-height: 1.5;
  margin: 0;
`;

// Modifico la struttura della sezione sfocata per aggiungere un lucchetto
const BlurredSection = styled.div`
  position: relative;
  filter: blur(5px);
  user-select: none;
  pointer-events: none;
  opacity: 0.6;
  margin-top: 0;
  padding-top: 20px;
`;

// Aggiungo un overlay con lucchetto
const LockOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 15;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(26, 12, 46, 0.75);
  border-radius: 10px;
`;

const LockIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 64, 192, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff40c0;
  font-size: 2.5rem;
  margin-bottom: 20px;
`;

const LockMessage = styled.p`
  font-size: 1.3rem;
  color: white;
  text-align: center;
  max-width: 500px;
  margin: 0 auto 20px;
  
  span {
    color: #ff40c0;
    font-weight: 700;
  }
`;

const RegistrationOverlay = styled.div`
  position: absolute;
  top: -80px;
  left: 0;
  right: 0;
  background: linear-gradient(
    to bottom,
    rgba(26, 12, 46, 0) 0%,
    rgba(26, 12, 46, 0.8) 20%,
    rgba(26, 12, 46, 0.95) 40%
  );
  padding: 100px 20px 40px;
  text-align: center;
  border-radius: 0 0 20px 20px;
  z-index: 10;
`;

const RegistrationText = styled.p`
  font-size: 1.3rem;
  color: #fff;
  margin-bottom: 20px;
  font-weight: 500;
`;

const RegistrationHighlight = styled.span`
  color: #ff40c0;
  font-weight: 700;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 25px;
  
  @media (max-width: 600px) {
    flex-direction: column;
    max-width: 280px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const RegisterButton = styled.button`
  flex: 1;
  background: linear-gradient(45deg, #ff40c0, #ff65d5);
  color: white;
  border: none;
  border-radius: 30px;
  padding: 15px 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(255, 64, 192, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 64, 192, 0.5);
  }
  
  @media (max-width: 600px) {
    width: 100%;
  }
`;

const LoginButton = styled.button`
  flex: 1;
  background: transparent;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 30px;
  padding: 15px 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }
  
  @media (max-width: 600px) {
    width: 100%;
  }
`;

// Aggiungo un banner superiore per i pulsanti register/login
const TopBanner = styled.div`
  width: 100%;
  padding: 15px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-bottom: 10px;
  text-align: center;
`;

const BannerText = styled.h2`
  font-size: 1.2rem;
  color: white;
  margin: 0;
  
  span {
    color: #ff40c0;
    font-weight: 700;
  }
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 15px;
  max-width: 800px;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const PrimaryButtonLarge = styled.button`
  flex: 1;
  background: #ff40c0;
  color: white;
  border: none;
  border-radius: 30px;
  padding: 14px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    transform: translateY(-2px);
    background: #ff56c9;
  }
`;

const SecondaryButtonLarge = styled.button`
  flex: 1;
  background: transparent;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 30px;
  padding: 14px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

// La sezione è già definita sopra

// Aggiungo componenti per simulare testo censurato
const CensoredTextLine = styled.div`
  height: ${props => props.height || '18px'};
  width: ${props => props.width || '100%'};
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  margin-bottom: ${props => props.marginBottom || '10px'};
  filter: blur(3px);
`;

const CensoredContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  pointer-events: none;
  padding: 0 10px;
`;

const CensoredSection = styled.div`
  margin-bottom: 30px;
`;

const CensoredSectionTitle = styled.div`
  height: 30px;
  width: 40%;
  background-color: rgba(255, 64, 192, 0.2);
  border-radius: 6px;
  margin-bottom: 20px;
  filter: blur(3px);
`;

const CensoredPersonalityItem = styled.div`
  display: flex;
  margin-bottom: 25px;
`;

const CensoredEmoji = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: rgba(255, 64, 192, 0.3);
  margin-right: 12px;
  filter: blur(2px);
`;

const CensoredPersonalityContent = styled.div`
  flex: 1;
`;

const CensoredPersonalityTitle = styled.div`
  height: 20px;
  width: 50%;
  background-color: rgba(255, 64, 192, 0.2);
  border-radius: 4px;
  margin-bottom: 10px;
  filter: blur(3px);
`;

const AnalyzeDemo = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [context, setContext] = useState('general');
  const [specificGoal, setSpecificGoal] = useState('');
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const analysisRef = useRef(null);
  const { showError } = useToast();
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };
  
  const handleFiles = (files) => {
    const validFiles = files.filter(file => 
      file.type.startsWith('image/')
    ).slice(0, 6 - selectedFiles.length);
    
    if (validFiles.length > 0) {
      const newFiles = validFiles.map(file => ({
        file,
        preview: URL.createObjectURL(file)
      }));
      
      setSelectedFiles(prev => [...prev, ...newFiles].slice(0, 6));
    }
  };
  
  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };
  
  const handleRemoveImage = (index) => {
    setSelectedFiles(prev => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };
  
  const handleAnalyzeClick = () => {
    if (selectedFiles.length === 0) {
      showError('Please upload at least one image to analyze');
      return;
    }
    
    // Mostra l'analisi parziale invece di mostrare il modale di registrazione immediatamente
    setShowAnalysis(true);
    
    // Scroll verso l'analisi
    setTimeout(() => {
      if (analysisRef.current) {
        analysisRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  
  const handleCloseModal = () => {
    setShowRegisterModal(false);
  };
  
  const handleRegisterClick = () => {
    navigate('/register');
  };
  
  const handleLoginClick = () => {
    navigate('/login');
  };
  
  const handleUploadClick = () => {
    fileInputRef.current.click();
  };
  
  return (
    <DemoContainer>
      <PageContainer>
        <AnalysisConfigSection>
          <AnalysisCard>
            <CardHeader>
              <InstagramIcon />
              <CardTitle>Preview</CardTitle>
            </CardHeader>
            
            <CardContent>
              {selectedFiles.length === 0 ? (
                <UploadArea 
                  className={isDragging ? 'dragover' : ''}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleUploadClick}
                >
                  <UploadIcon>
                    <CloudUploadIcon />
                  </UploadIcon>
                  <UploadText>
                    Upload profile screenshots or drop files here (Max 6)
                  </UploadText>
                  <FileInput
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileInputChange}
                    accept="image/*"
                    multiple
                  />
                </UploadArea>
              ) : (
                <>
                  <ImagePreviewArea>
                    {selectedFiles.map((file, index) => (
                      <ImagePreview 
                        key={index} 
                        style={{ backgroundImage: `url(${file.preview})` }}
                        onClick={() => handleRemoveImage(index)}
                      />
                    ))}
                  </ImagePreviewArea>
                </>
              )}
            </CardContent>
            
            <CardFooter>
              <ActionButton 
                onClick={handleUploadClick}
                disabled={selectedFiles.length >= 6}
              >
                <FiUpload /> Upload More
              </ActionButton>
            </CardFooter>
          </AnalysisCard>
          
          <CustomizeSection>
            <SectionTitle>Customize your analysis</SectionTitle>
            
            <ContextSelector>
              <Label>Context</Label>
              <Select>
                <select 
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                >
                  <option value="general">General</option>
                  <option value="dating">Dating</option>
                  <option value="professional">Professional</option>
                  <option value="friendship">Friendship</option>
                </select>
              </Select>
            </ContextSelector>
            
            <div>
              <Label>Specific goal (optional)</Label>
              <TextArea 
                placeholder="Enter a specific goal for the analysis, for example: 'Analyze the presence of hobbies or sports interests' (Max 6 images)"
                value={specificGoal}
                onChange={(e) => setSpecificGoal(e.target.value)}
              />
            </div>
            
            <DisclaimerBox>
              <FiInfo />
              <DisclaimerText>
                Please note: This analysis is based solely on visual content from social media profiles. People may present themselves differently online than in real life. <span>This is not a certified professional analysis</span> and should be used for entertainment purposes only.
              </DisclaimerText>
            </DisclaimerBox>
            
            <AnalyzeButton onClick={handleAnalyzeClick}>
              Analyze <FiArrowRight />
            </AnalyzeButton>
          </CustomizeSection>
        </AnalysisConfigSection>
      </PageContainer>
      
      {/* Sezione per l'analisi parziale */}
      {showAnalysis && (
        <AnalysisResultsSection ref={analysisRef} show={showAnalysis}>
          <AnalysisTitle>Analysis Results</AnalysisTitle>
          <AnalysisIntro>Here is an analysis of the Instagram profile based on the provided image:</AnalysisIntro>
          
          <SectionTitle>Brief General Description</SectionTitle>
          <AnalysisParagraph>
            Based on the images you've uploaded, this profile reveals specific visual and content patterns 
            that provide insights into the person's social media presence. The overall presentation suggests 
            certain characteristics about their online persona and potentially their offline interests and personality.
          </AnalysisParagraph>
          
          <SectionTitle>Perceived Personality</SectionTitle>
          
          {/* Inizio della parte censurata subito dopo il titolo */}
          <div style={{ position: 'relative', marginTop: '20px', minHeight: '600px' }}>
            {/* Contenuto censurato simulato */}
            <CensoredContent>
              {/* Sezione personalità */}
              <CensoredSection>
                <CensoredPersonalityItem>
                  <CensoredEmoji />
                  <CensoredPersonalityContent>
                    <CensoredPersonalityTitle />
                    <CensoredTextLine width="95%" />
                    <CensoredTextLine width="90%" />
                    <CensoredTextLine width="40%" />
                  </CensoredPersonalityContent>
                </CensoredPersonalityItem>
                
                <CensoredPersonalityItem>
                  <CensoredEmoji />
                  <CensoredPersonalityContent>
                    <CensoredPersonalityTitle />
                    <CensoredTextLine width="98%" />
                    <CensoredTextLine width="87%" />
                    <CensoredTextLine width="55%" />
                  </CensoredPersonalityContent>
                </CensoredPersonalityItem>
                
                <CensoredPersonalityItem>
                  <CensoredEmoji />
                  <CensoredPersonalityContent>
                    <CensoredPersonalityTitle />
                    <CensoredTextLine width="92%" />
                    <CensoredTextLine width="85%" />
                  </CensoredPersonalityContent>
                </CensoredPersonalityItem>
              </CensoredSection>
              
              {/* Sezione compatibilità */}
              <CensoredSectionTitle />
              <CensoredTextLine width="85%" marginBottom="20px" />
              
              <CensoredPersonalityItem>
                <CensoredEmoji />
                <CensoredPersonalityContent>
                  <CensoredPersonalityTitle />
                  <CensoredTextLine width="95%" />
                  <CensoredTextLine width="87%" />
                  <CensoredTextLine width="70%" />
                </CensoredPersonalityContent>
              </CensoredPersonalityItem>
              
              <CensoredPersonalityItem>
                <CensoredEmoji />
                <CensoredPersonalityContent>
                  <CensoredPersonalityTitle />
                  <CensoredTextLine width="93%" />
                  <CensoredTextLine width="88%" />
                  <CensoredTextLine width="45%" />
                </CensoredPersonalityContent>
              </CensoredPersonalityItem>
              
              {/* Altra sezione */}
              <CensoredSectionTitle />
              <CensoredTextLine width="70%" marginBottom="20px" />
              
              <CensoredTextLine width="80%" height="22px" />
              <CensoredTextLine width="60%" height="22px" marginBottom="25px" />
              
              <CensoredTextLine width="82%" height="22px" />
              <CensoredTextLine width="58%" height="22px" marginBottom="25px" />
            </CensoredContent>
            
            {/* Utilizzo placeholder invece del testo reale per evitare che sia visibile all'ispezione */}
            <div aria-hidden="true" style={{ visibility: 'hidden', height: 0, overflow: 'hidden' }}>
              Content only available after registration
            </div>
            
            <LockOverlay>
              <LockIcon>
                <FiLock />
              </LockIcon>
              <LockMessage>
                This content is <span>locked</span>. Register or log in to unlock the complete analysis.
              </LockMessage>
              <div style={{ display: 'flex', justifyContent: 'center', width: '100%', maxWidth: '300px' }}>
                <RegisterButton onClick={handleRegisterClick} style={{ width: '100%' }}>
                  <FiUser /> Register for free
                </RegisterButton>
              </div>
            </LockOverlay>
          </div>
        </AnalysisResultsSection>
      )}
      
      {showRegisterModal && (
        <RegisterModal onClose={handleCloseModal} />
      )}
    </DemoContainer>
  );
};

export default AnalyzeDemo; 